import type { CityConfig } from "../city-template/types";

export const kokomoConfig: CityConfig = {
  city: "Kokomo",
  state: "Indiana",
  stateAbbr: "IN",
  slug: "kokomo",
  route: "/cities/kokomo",
  title: "Emergency Passport Kokomo IN | Expedited Passport Howard County | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Kokomo, Indiana — as fast as 24 hours. Fast passport renewal and new passport applications for Kokomo, Logansport, Peru, Tipton, and all Howard County residents. A+ BBB rated registered courier serving North Central Indiana.",
  h1: "Emergency & Expedited Passport Services in Kokomo, IN",
  heroSubtitle: "Kokomo travelers — from automotive manufacturing executives heading to international plants to Indiana University Kokomo students studying abroad to Howard County families planning vacations — trust Fast Passport Center for emergency and expedited passports as fast as 24 hours. A+ BBB rated registered courier with direct access to the Indianapolis Passport Agency, about an hour south via US-31.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Kokomo%20Indiana%20downtown%20cityscape%20with%20historic%20Howard%20County%20Courthouse%20and%20revitalized%20Main%20Street%2C%20automotive%20manufacturing%20heritage%20city%20with%20modern%20developments%2C%20tree%20lined%20streets%20in%20a%20classic%20Midwest%20industrial%20city%2C%20warm%20afternoon%20sunlight%2C%20professional%20urban%20photography%20with%20rich%20warm%20tones%2C%20classic%20American%20heartland%20city%20character%2C%20no%20text%20or%20people%2C%20editorial%20style%20urban%20photography&width=1800&height=700&seq=kokomo-in-hero-001&orientation=landscape",
  heroImageAlt: "Kokomo Indiana downtown with Howard County Courthouse and historic Main Street",
  geoPosition: "40.4864;-86.1336",
  geoPlacename: "Kokomo, Indiana",
  lastModified: "2026-06-08",
  regionalAgency: {
    name: "Indianapolis Passport Agency",
    address: "4419 Indiana Ave, Indianapolis, IN 46221",
    distance: "Approximately 1 hour south of Kokomo via US-31 — about 52 miles",
  },
  nearbyCitiesZips: [
    { name: "Kokomo", zips: "46901, 46902, 46903, 46904" },
    { name: "Logansport", zips: "46947" },
    { name: "Peru", zips: "46970" },
    { name: "Tipton", zips: "46072" },
    { name: "Russiaville", zips: "46979" },
    { name: "Greentown", zips: "46936" },
    { name: "Galveston", zips: "46932" },
    { name: "Sharpsville", zips: "46068" },
    { name: "Windfall", zips: "46076" },
    { name: "Howard County rural area", zips: "46901, 46902, 46979" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Registered courier to Indianapolis Passport Agency — about 1 hour from Kokomo via US-31" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting for Howard County" },
    { icon: "ri-building-line", title: "North Central Indiana Hub", desc: "Serving Kokomo's manufacturing community, IU Kokomo, and Howard County families" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels from any UPS or FedEx in Kokomo or Logansport" },
  ],
  internalLinks: [
    { name: "Expedited passport in Indianapolis, IN", route: "/cities/indianapolis" },
    { name: "Emergency passport help in Fort Wayne, IN", route: "/cities/fort-wayne" },
    { name: "Urgent passport services in Carmel, IN", route: "/cities/carmel" },
    { name: "Indiana passport services overview", route: "/states/indiana" },
  ],
  fpcOfficeAddress: "4419 Indiana Ave, Indianapolis, IN 46221",
  fpcOfficeNote: "Fast Passport Center serves Kokomo, Indiana via secure registered courier to the Indianapolis Passport Agency (4419 Indiana Ave, Indianapolis). Approximately 52 miles south — about an hour via US-31. We handle the drive so Kokomo residents, automotive industry travelers, and Howard County families never need to make the trip themselves.",
  fpcOfficeDistance: "Serving Kokomo, Logansport, Peru, Tipton, Russiaville, Greentown, Galveston, Sharpsville, and all of Howard County and the surrounding north central Indiana region",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Kokomo and Howard County. From the historic downtown and automotive manufacturing facilities to IU Kokomo and the surrounding rural communities — we serve all of Howard County and north central Indiana.",
  nearbyCitiesIntro: "We serve Kokomo and all nearby communities including Logansport, Peru, Tipton, Russiaville, Greentown, and Galveston. We also serve all of Howard County and the IU Kokomo community.",
  howItWorksIntro: "Most Kokomo travelers — especially automotive manufacturing executives and IU Kokomo students — complete everything online and ship documents via overnight courier from any UPS or FedEx in Kokomo. Our registered couriers handle the Indianapolis Passport Agency delivery.",
  mistakesIntro: "Kokomo is a city built on manufacturing and innovation, with significant international business travel to automotive plants worldwide. Here are the most common passport mistakes we see from Howard County travelers.",
  testimonialsIntro: "Kokomo travelers — from automotive executives to IU Kokomo students to Howard County families — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Kokomo, IN? Here are the most common ones we hear from Howard County travelers.",
  statePageRoute: "/states/indiana",
  statePageName: "Indiana",
  dmvWebsite: "https://www.in.gov/bmv",
  dmvLabel: "Indiana BMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive to Indianapolis Passport Agency (~1 hour each way)", "Limited appointments for non-emergency travelers"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "No Indianapolis drive required — ship from Kokomo", "Perfect for automotive industry travel, IU Kokomo study abroad, and family vacations", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Faster than self-service", "Good for planned international travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Kokomo travelers with immediate departure needs — as fast as 24 hours. Ideal for automotive manufacturing executives with last-minute international plant visits or IU Kokomo students with urgent study abroad deadlines.",
      bullets: ["24–48 hour turnaround available", "Indianapolis Passport Agency — courier handles the 1-hour drive", "Manufacturing and college travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Kokomo adults with a passport issued within the last 15 years. Skip the post office appointment — ideal for Howard County professionals and families.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to your Kokomo address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Kokomo residents, IU Kokomo students, and Howard County families.",
      bullets: ["Acceptance facility scheduling in Kokomo", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Kokomo and Howard County residents — whether lost during travel, misplaced at home, or stolen while preparing for a trip.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Kokomo and Howard County families traveling internationally with kids.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Kokomo and Howard County residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Kokomo IN", description: "Urgent passport processing for Kokomo Indiana travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Kokomo IN", description: "Fast passport renewal for Kokomo Indiana residents with a previously-issued passport." },
    { name: "First-Time Passport Kokomo IN", description: "New passport application services for Kokomo Indiana residents and IU Kokomo students." },
    { name: "Child Passport Kokomo IN", description: "Minor passport services for Kokomo Indiana families with children under 16." },
  ],
  testimonials: [
    {
      name: "Greg P.",
      location: "Kokomo, IN — Automotive Engineer",
      rating: 5,
      text: "Got called to troubleshoot a transmission line issue at our plant in Germany with 6 days notice. My passport had expired during COVID. Fast Passport Center renewed it in 3 days. Shipped from the UPS Store on Markland Avenue. Made my flight from Indianapolis without a problem.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "Nicole W.",
      location: "Kokomo, IN — IU Kokomo Student",
      rating: 5,
      text: "Accepted into a summer research program in Costa Rica with 12 days before departure. Never had a passport before. Fast Passport Center walked me through the first-time application and my passport arrived in 5 days. Incredible service for students.",
      service: "First-Time Passport",
    },
    {
      name: "Frank & Donna S.",
      location: "Kokomo, IN — Near Foster Park",
      rating: 5,
      text: "Retirement trip to Ireland with friends. Both our passports were expired and we had 3 weeks until departure. Fast Passport Center processed both renewals in 6 days. We were so relieved. Shipped from FedEx on South Reed Road. Everything was seamless.",
      service: "Double Passport Renewal",
    },
    {
      name: "Mike D.",
      location: "Logansport, IN — Near Kokomo",
      rating: 5,
      text: "Plant manager visiting our facility in Monterrey, Mexico with 10 days notice. My passport had less than 3 months validity. Fast Passport Center expedited a renewal and I had it in 4 days. Made the trip without any disruption to the production schedule.",
      service: "Expedited Business Renewal",
    },
  ],
  facilities: [
    {
      id: "kokomo-in-1",
      name: "Kokomo Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2719 S Webster St",
      city: "Kokomo",
      state: "IN",
      zip: "46902",
      phone: "(765) 453-3835",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "South Kokomo, near Markland Mall area",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Kokomo acceptance facility. Appointments required — book 2–3 weeks ahead during peak travel seasons. Convenient for south Kokomo and IU Kokomo students.",
      icon: "ri-mail-line",
    },
    {
      id: "kokomo-in-2",
      name: "Howard County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "104 N Buckeye St",
      city: "Kokomo",
      state: "IN",
      zip: "46901",
      phone: "(765) 456-2204",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "Downtown Kokomo, Howard County Courthouse",
      website: "https://www.howardcountyin.gov/",
      websiteLabel: "Howard County",
      notes: "County clerk passport acceptance. Extended morning hours. Good alternative when post office appointments are full. Convenient for downtown Kokomo workers.",
      icon: "ri-government-line",
    },
    {
      id: "kokomo-in-3",
      name: "Logansport Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "300 E Broadway",
      city: "Logansport",
      state: "IN",
      zip: "46947",
      phone: "(574) 722-1235",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 25 miles north of Kokomo, in Logansport",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative acceptance facility north of Kokomo. Convenient for Logansport and Peru area residents.",
      icon: "ri-mail-line",
    },
    {
      id: "kokomo-in-4",
      name: "Tipton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "210 S West St",
      city: "Tipton",
      state: "IN",
      zip: "46072",
      phone: "(765) 675-7561",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 15 miles south of Kokomo, in Tipton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves southern Howard County and Tipton County. Smaller facility — call ahead for appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "kokomo-in-5",
      name: "Peru Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 N Huntington St",
      city: "Peru",
      state: "IN",
      zip: "46970",
      phone: "(765) 473-6221",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 20 miles north of Kokomo, in Peru",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative acceptance facility in Peru. Convenient for Miami County and northern Howard County residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, 2–3 week wait", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 1-hour drive to Indianapolis via US-31", fpc: "No — ship from Kokomo, courier handles it" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Automotive Industry Travel", agency: "No special support for manufacturing travelers", fpc: "Experienced with automotive industry international deadlines" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Kokomo's automotive industry creates frequent international travel to plants in Germany, Mexico, Japan, and China. These trips are often scheduled on short notice — sometimes with just 7–14 days lead time. Routine passport processing takes 8–11 weeks. Start expedited service as soon as travel is confirmed.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Passport Photo",
      description: "Photos from local drugstores sometimes fail State Department standards. Kokomo's Walgreens and CVS locations offer passport photos, but always verify they meet the exact 2x2 inch, white background, no-glasses specification.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "First-time applicants in Kokomo — especially IU Kokomo students — often miss signatures or use the wrong form. DS-11 requires in-person execution at an acceptance facility. DS-82 can be mailed for most renewals.",
    },
    {
      icon: "ri-calendar-line",
      title: "Not Anticipating IU Kokomo Semester Deadlines",
      description: "IU Kokomo students participating in study abroad programs often discover passport issues after acceptance. Check your passport status as soon as you apply for any international program.",
    },
    {
      icon: "ri-global-line",
      title: "Not Checking 6-Month Passport Validity",
      description: "Many countries — especially in Europe and Asia — require 6 months of passport validity beyond your stay. Automotive engineers heading to German plants or suppliers in Japan often discover this requirement at the last minute.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Assuming Indianapolis Agency Accepts Walk-Ins",
      description: "The Indianapolis Passport Agency — about an hour south of Kokomo — requires appointments booked through NPIC. No walk-ins. Even though it's relatively close, you cannot simply show up. Courier service removes this uncertainty entirely.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from anywhere in Kokomo or Howard County." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS Store or FedEx in Kokomo, Logansport, or Peru." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for Howard County documentation errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Our registered courier delivers to the Indianapolis Passport Agency — no need to make the 1-hour drive." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Kokomo address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Kokomo, IN?", a: "Fast Passport Center offers emergency passport processing in Kokomo, Indiana in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Indianapolis Passport Agency (4419 Indiana Ave, Indianapolis) — about 52 miles south of Kokomo." },
    { q: "Does Kokomo have its own passport agency?", a: "No — Kokomo does not have a regional passport agency. The nearest is the Indianapolis Passport Agency, approximately 52 miles south (about an hour via US-31). Fast Passport Center's courier service means Kokomo and Howard County residents never need to make the drive themselves." },
    { q: "Can automotive industry travelers get expedited passports in Kokomo?", a: "Absolutely. Kokomo is a major automotive manufacturing hub and we regularly serve engineers, plant managers, and executives heading to facilities in Germany, Mexico, Japan, and China. We understand the compressed timelines of manufacturing travel and can coordinate expedited processing to match your flight schedule." },
    { q: "What about IU Kokomo students studying abroad?", a: "Yes. Indiana University Kokomo students preparing for international programs are among our regular clients. Study abroad acceptance often comes with tight departure deadlines — we can process expedited passports for students with confirmed travel plans." },
    { q: "What airport do Kokomo travelers use?", a: "Indianapolis International Airport (IND) is the primary airport for Kokomo travelers, located about 65 miles south (approximately 1 hour and 10 minutes via US-31 and I-465). IND offers direct flights to Toronto, Cancun, and Paris (seasonal), plus connecting service worldwide through major hubs." },
    { q: "How much does an expedited passport cost in Kokomo, IN?", a: "Government fees are fixed: $130 for a first-time adult passport book or $130 + $60 expedite fee for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your specific departure timeline." },
    { q: "Where can I get passport photos in Kokomo?", a: "Walgreens (multiple locations including 1725 E Markland Ave and 2108 E Boulevard), CVS (2130 E Markland Ave), and the UPS Store (multiple locations) all offer passport photo services in Kokomo. Photos must be 2x2 inches on a white background, taken within 6 months." },
    { q: "Does Fast Passport Center serve Logansport, Peru, or Tipton residents?", a: "Absolutely. Fast Passport Center serves all of Howard County and surrounding areas including Logansport, Peru, Tipton, Russiaville, Greentown, Galveston, and Sharpsville. Our courier service works the same way for everyone throughout north central Indiana." },
  ],
  photoLocations: [
    { name: "Walgreens — Kokomo (Markland Ave)", address: "1725 E Markland Ave, Kokomo, IN 46901", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS — Kokomo (Markland Ave)", address: "2130 E Markland Ave, Kokomo, IN 46901", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Kokomo", address: "1718 E Markland Ave, Kokomo, IN 46901", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–3PM" },
    { name: "Walgreens — Kokomo (E Boulevard)", address: "2108 E Boulevard, Kokomo, IN 46902", hours: "Mon–Sun: 7AM–10PM" },
  ],
  dmvLocations: [
    { name: "Indiana BMV — Kokomo", address: "500 N Buckeye St, Kokomo, IN 46901", hours: "Mon–Fri: 8:30AM–6:00PM", note: "Indiana Bureau of Motor Vehicles for driver's licenses and REAL ID. Online reservations strongly recommended. Convenient north Kokomo location near downtown.", featured: true },
    { name: "Indiana BMV — Logansport", address: "3901 E Market St, Logansport, IN 46947", hours: "Mon–Fri: 8:30AM–6:00PM", note: "Serves Logansport and Cass County. Alternative when Kokomo BMV appointments are full. REAL ID processing available.", featured: false },
  ],
  airports: [
    {
      code: "IND",
      name: "Indianapolis International Airport",
      distance: "65 miles south of Kokomo via US-31 and I-465 — approximately 1 hour and 10 minutes",
      address: "7800 Col. H. Weir Cook Memorial Dr, Indianapolis, IN 46241",
      note: "Primary airport for Kokomo and Howard County. Direct flights to Toronto, Cancun, and Paris (seasonal). Connecting service worldwide through major hubs. The main gateway for Kokomo international travelers.",
      flightUrl: "https://www.ind.com/",
      flightLabel: "IND Flight Info",
      featured: true,
    },
    {
      code: "FWA",
      name: "Fort Wayne International Airport",
      distance: "65 miles northeast of Kokomo via I-69 — approximately 1 hour and 15 minutes",
      address: "3801 W Ferguson Rd, Fort Wayne, IN 46809",
      note: "Alternative airport for Kokomo travelers. Domestic connections to major hubs including Atlanta, Charlotte, Chicago, and Detroit. Good option when IND routes don't match your destination.",
      flightUrl: "https://www.flyfwa.com/",
      flightLabel: "FWA Flight Info",
      featured: false,
    },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Howard County: Birth & Death Certificates", url: "https://www.in.gov/health/vital-records/", icon: "ri-file-text-line", description: "Indiana Department of Health Vital Records — order birth and death certificates for Howard County and all Indiana counties" },
    { label: "Howard County Clerk: Marriage Licenses", url: "https://www.howardcountyin.gov/", icon: "ri-heart-line", description: "Howard County Clerk — marriage license records for Kokomo and Howard County" },
    { label: "Howard County: Divorce Decrees", url: "https://www.howardcountyin.gov/", icon: "ri-file-list-3-line", description: "Howard County Clerk of Courts — divorce decree records for Kokomo and Howard County" },
    { label: "VitalChek Nationwide Service", url: "https://www.vitalchek.com/", icon: "ri-global-line", description: "Order Indiana vital records online through VitalChek for delivery to your Kokomo address" },
  ],
};